import { useState, useEffect, useCallback } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useConfig } from 'builder/hooks/useConfig'
import { ButtonTheme } from 'builder/components/Button'
import { ApiPlan, AvailablePlansResponse } from 'builder/modules/billing/types'
import { Spinner } from 'builder/components/Spinner'
import ModalOverlay from '../ModalOverlay'
import { PaymentForm } from './PaymentForm'
import CrownImage from './assets/crown.png'
import CLOCK from './assets/icons/clock.svg'
import DOCUMENT from './assets/icons/document.svg'
import DOCUMENTS from './assets/icons/documents.svg'
import GROUP from './assets/icons/group.svg'
import MONEY from './assets/icons/money.svg'
import TIMER from './assets/icons/timer.svg'

import {
  Container,
  CloseButton,
  LeftSection,
  RightSection,
  Crown,
  Title,
  Subtitle,
  FeatureList,
  FeatureItem,
  FeatureIcon,
  FeatureText,
  PricingOption,
  RadioButton,
  PricingOptionContent,
  PricingDetails,
  PricingPeriod,
  PricingAmount,
  SaveBadge,
  NextButton,
} from './styles'

type ModalStep = 'plan-selection' | 'payment'

interface Feature {
  icon: string
  boldText?: string
  text?: string
  email?: string
  phone?: string
  pricingData?: {
    text: string
    hasDiscount: boolean
    beforePrice?: string
    afterPrice?: string
    originalPrice?: string
    discountedPrice?: string
  }
  hideWrap?: boolean
}

interface UpgradeModalProps {
  onClose: () => void
  onPaymentSuccess: (piToken: string) => void
  onPaymentError?: (error: unknown) => void
}

export const UpgradeModal = ({ onClose, onPaymentSuccess, onPaymentError }: UpgradeModalProps) => {
  const { isPhone } = useMediaQueries()
  const config = useConfig()
  const [selectedPlan, setSelectedPlan] = useState('')
  const [currentStep, setCurrentStep] = useState<ModalStep>('plan-selection')
  const [plans, setPlans] = useState<ApiPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hostname = window.location.hostname
  const isCareerIo = hostname === 'career.io' || hostname === 'staging.career.io'

  useEffect(() => {
    trackInternalEvent('show_plans_modal')
  }, [])

  const handleNext = () => {
    trackInternalEvent('click_next_plans_modal')
    setCurrentStep('payment')
  }

  const handleBackToPlanSelection = () => {
    setCurrentStep('plan-selection')
  }

  const handlePaymentSuccess = (piToken: string) => {
    onPaymentSuccess?.(piToken)
  }

  const handlePaymentError = (error: unknown) => {
    onPaymentError?.(error)
  }

  const generatePricingText = (plan: ApiPlan | undefined) => {
    if (!plan) return { text: '', hasDiscount: false }
    if (!plan.isRecurring) return { text: 'One-time payment', hasDiscount: false }

    const isMonthly =
      (plan.interval === 'month' && plan.intervalCount === 1) ||
      (plan.interval === 'week' && plan.intervalCount === 4)
    const isQuarterly = plan.interval === 'month' && plan.intervalCount === 3
    const isYearly = plan.interval === 'year' && plan.intervalCount === 1
    const hasTrial = plan.hasTrial && plan.trialDays && plan.trialDays > 0
    const trialDays = plan.trialDays || 0
    const originalPrice = plan.formattedAmountBeforeDiscount || plan.formattedRecurringAmount
    const discountedPrice = plan.formattedRecurringAmount

    const trialText = hasTrial ? ` after ${trialDays} days` : ''
    let billingText = ''

    if (isMonthly) billingText = '(billed monthly)'
    else if (isQuarterly) billingText = 'billed quarterly'
    else if (isYearly) billingText = '(billed yearly)'

    const baseText = isQuarterly ? 'Auto-renews to ' : 'Auto-renews at '

    if (plan.hasDiscount) {
      return {
        text: `${baseText}${trialText} ${billingText}`,
        beforePrice: baseText,
        afterPrice: `${trialText} ${billingText}`,
        originalPrice,
        discountedPrice,
        hasDiscount: true,
      }
    }

    return {
      text: `${baseText}${originalPrice}${trialText} ${billingText}`,
      hasDiscount: false,
    }
  }

  const selectedPlanData = plans.find(plan => plan.name === selectedPlan)
  const pricingData = generatePricingText(selectedPlanData)

  const baseFeatures = {
    unlimited: {
      icon: DOCUMENTS,
      boldText: 'Unlimited',
      text: 'resumes and cover letters',
    },
    templates: {
      icon: DOCUMENT,
      text: 'Access 500+ professional resume templates',
    },
    recruiters: {
      icon: GROUP,
      text: 'Match and send your resume to 50 recruiters',
    },
    pricing: {
      icon: CLOCK,
      text: pricingData.text,
      pricingData,
      hideWrap: true,
    },
    guarantee: {
      icon: MONEY,
      boldText: 'Money Back Guarantee',
    },
    cancel: {
      icon: TIMER,
      boldText:
        config?.features?.geoipCode === 'US'
          ? 'Cancel any time online or call'
          : 'Cancel any time online or by email',
      ...(config?.features?.geoipCode === 'US'
        ? { phone: '+1 (833) 998-8370' }
        : { email: `support@${isCareerIo ? 'career' : 'resume'}.io` }),
    },
  }

  const allFeatures: Feature[] = [
    baseFeatures.unlimited,
    baseFeatures.templates,
    baseFeatures.recruiters,
    baseFeatures.pricing,
    baseFeatures.guarantee,
    baseFeatures.cancel,
  ]

  const phoneFeatures: Feature[] = [
    baseFeatures.pricing,
    baseFeatures.guarantee,
    baseFeatures.cancel,
  ]

  const features = isPhone ? phoneFeatures : allFeatures

  const fetchPlans = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch('/api/app/billing/plans')

      if (!response.ok) {
        throw new Error('Failed to fetch plans')
      }

      const data: AvailablePlansResponse = await response.json()
      setPlans(data.plans)

      if (data.plans.length > 0) {
        setSelectedPlan(prev => prev || data.plans[0].name)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  const formatPlanAmount = (plan: ApiPlan) => {
    if (plan.hasTrial && plan.trialDays && plan.trialDays > 0) {
      return `${plan.currencySymbol}${((plan.amount || 0) / 100).toFixed(2)}`
    }
    return plan.formattedRecurringAmount
  }

  const formatPlanPeriod = (plan: ApiPlan) => {
    if (plan.hasTrial && plan.trialDays && plan.trialDays > 0) {
      return `${plan.trialDays}-day Trial`
    }

    if (plan.caption && plan.caption.trim() !== '') {
      return plan.caption
    }

    if (plan.interval && plan.intervalCount) {
      const count = plan.intervalCount
      const interval = plan.interval

      if (count === 1) {
        return `1 ${interval}`
      } else {
        const pluralInterval = `${interval}s`
        return `${count} ${pluralInterval}`
      }
    }

    return plan.name
  }

  const getSaveBadge = (plan: ApiPlan) => {
    if (!plan.hasDiscount || !plan.amountBeforeDiscount) {
      return
    }

    const originalAmount = plan.amountBeforeDiscount
    const currentAmount = plan.isRecurring
      ? plan.recurringAmount
      : plan.amount || plan.recurringAmount

    if (originalAmount <= currentAmount) {
      return
    }

    const discountPercentage = Math.round(((originalAmount - currentAmount) / originalAmount) * 100)
    return `Save ${discountPercentage}%`
  }

  const pricingOptions = plans.map(plan => ({
    id: plan.name,
    period: formatPlanPeriod(plan),
    amount: formatPlanAmount(plan),
    selected: selectedPlan === plan.name,
    badge: getSaveBadge(plan),
  }))

  const selectedPricingOption = pricingOptions.find(option => option.id === selectedPlan)

  const renderLeftSection = () => (
    <LeftSection>
      <Crown src={CrownImage} alt="Crown" />
      <Title>Upgrade and get hired</Title>
      <Subtitle>Upgrade your membership today and land a job you love sooner!</Subtitle>

      <FeatureList>
        {features.map((feature, index) => (
          <FeatureItem key={index} $hideWrap={!!feature.hideWrap}>
            <FeatureIcon src={feature.icon} />
            {feature.boldText && <FeatureText className="bold">{feature.boldText}</FeatureText>}
            {feature.email && <FeatureText className="email">{feature.email}</FeatureText>}
            {feature.phone && <FeatureText className="email">{feature.phone}</FeatureText>}

            {feature.pricingData ? (
              <FeatureText>
                {feature.pricingData.hasDiscount ? (
                  <>
                    {feature.pricingData.beforePrice}
                    <del>{feature.pricingData.originalPrice}</del>{' '}
                    <strong>{feature.pricingData.discountedPrice}</strong>
                    {feature.pricingData.afterPrice}
                  </>
                ) : (
                  feature.pricingData.text
                )}
              </FeatureText>
            ) : (
              <FeatureText>{feature.text}</FeatureText>
            )}
          </FeatureItem>
        ))}
      </FeatureList>
    </LeftSection>
  )

  const renderPlanSelection = () => {
    if (isLoading) {
      return (
        <>
          {renderLeftSection()}
          <RightSection $isLoading={isLoading} $isPayment={false}>
            <Spinner $width="64px" $height="64px" />
          </RightSection>
        </>
      )
    }

    if (error) {
      return (
        <>
          {renderLeftSection()}
          <RightSection $isLoading={isLoading} $isPayment={false}>
            <div>Error loading plans: {error}</div>
          </RightSection>
        </>
      )
    }

    return (
      <>
        {renderLeftSection()}

        <RightSection $isLoading={isLoading} $isPayment={false}>
          {pricingOptions.map(option => (
            <PricingOption
              key={option.id}
              $selected={option.selected}
              onClick={() => setSelectedPlan(option.id)}
              $marginTop={!!option.badge}
            >
              {option.badge && <SaveBadge>{option.badge}</SaveBadge>}
              <PricingOptionContent>
                <RadioButton $selected={option.selected} />
                <PricingDetails>
                  <PricingPeriod>{option.period}</PricingPeriod>
                  <PricingAmount>{option.amount}</PricingAmount>
                </PricingDetails>
              </PricingOptionContent>
            </PricingOption>
          ))}

          <NextButton
            onClick={handleNext}
            theme={ButtonTheme.default}
            disabled={!selectedPlan || isLoading}
          >
            Next
          </NextButton>
        </RightSection>
      </>
    )
  }

  const renderPaymentForm = () => {
    if (!selectedPlanData) {
      return (
        <>
          {renderLeftSection()}
          <RightSection $isLoading={isLoading} $isPayment={true}>
            <div>Error: Plan data not found</div>
          </RightSection>
        </>
      )
    }

    return (
      <>
        {renderLeftSection()}

        <RightSection $isLoading={isLoading} $isPayment={true}>
          <PaymentForm
            selectedPlan={selectedPlan}
            planAmount={selectedPricingOption?.amount || '$0.00'}
            planData={selectedPlanData}
            onBack={handleBackToPlanSelection}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        </RightSection>
      </>
    )
  }

  return (
    <ModalOverlay
      onClick={onClose}
      overlayFadeDuration={150}
      contentSlideDuration={150}
      fullScreen={isPhone}
    >
      <Container>
        <CloseButton
          onClick={() => {
            trackInternalEvent('close_plans_modal')
            onClose()
          }}
        />
        {currentStep === 'plan-selection' ? renderPlanSelection() : renderPaymentForm()}
      </Container>
    </ModalOverlay>
  )
}
